/**
 * AnalysisPanel (v2) — "Категории + группы" layout.
 *
 * Hero overall-score card on top, then collapsible section cards grouped under
 * semantic dividers (Оценка / Скрипт / Речь / Транскрипт). Layout mirrors the
 * Claude Design "Call Analysis Panel" template. NO Card wrappers from a UI kit —
 * the AnalysisSection shell (analysis-utils) is the single collapsible primitive.
 * All sections gracefully handle missing/undefined data (return null).
 *
 * Groups & cards:
 *  Оценка   → Score breakdown · Summary (SAO) · Content · Dialogue · Sale probability · Agreements
 *  Скрипт   → Script adherence — and nothing else, so the group disappears
 *             whole for an org that never attached a script
 *  Речь     → Speech analysis · Sentiment · Politeness · Clarity
 *  Инсайты  → Strengths & recommendations · Key moments · Compliance · Business intelligence
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { QualitySubMetricKey } from '@aica/core';
import { getProfileDisplayName } from '@/features/prompt-builder/profileDisplay';
import type {
  AnalysisVerificationState,
  VoiceMetrics,
  CrmStageProgression,
  RecordCriterionResult,
  BuiltinResultSources,
} from '@aica/api-types/schemas';
import type { XMLAnalysisResult, ComponentScores } from '../../AnalysisPanel';
import {
  AnalysisSection,
  CategoryFilter,
  confidenceLevelPercent,
  GroupDivider,
  HeroScore,
  WeightedAtAnalysisNote,
  scoreTextColor,
  type CategoryTab,
} from './analysis-utils';
import { AttentionFlagBlock } from './AttentionFlagBlock';
import { StageBadge } from './StageBadge';
import { VerificationBadge } from './VerificationBadge';
import { ProgressionChip } from './ProgressionChip';
import { QualityScoreSection } from './QualityScoreSection';
import { SummarySection } from './SummarySection';
import { ContentSection } from './ContentSection';
import { DialogueSection } from './DialogueSection';
import { SaleProbabilitySection } from './SaleProbabilitySection';
import { EmotionalSection } from './EmotionalSection';
import { SpeechSection } from './SpeechSection';
import { VoiceMetricsSection } from './VoiceMetricsSection';
import { ComplianceSection } from './ComplianceSection';
import { KeyMomentsSection } from './KeyMomentsSection';
import { RecommendationsSection } from './RecommendationsSection';
import { BusinessIntelligenceSection } from './BusinessIntelligenceSection';
import { AgreementsSection } from './AgreementsSection';
import { OtherCategoryNotice } from './OtherCategoryNotice';
import { VerificationNotice } from './VerificationNotice';
import { ScriptAdherenceTab, type ScriptAdherenceData } from '../../ScriptAdherenceTab';
import { CriteriaSection } from './CriteriaSection';
import {
  speechMetricsVisible,
  visibleCompliance,
} from '@/lib/records/builtin-legacy-rows';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DiarizationSegment {
  speaker: string;
  start: number;
  end: number;
  /**
   * Текст реплики. Панель сама его не рисует (транскрипт — отдельная вкладка),
   * но по нему группа «Критерии» находит момент цитаты; в данных он есть
   * всегда — прежде этот тип просто не объявлял поле, которым не пользовался.
   */
  text?: string;
}

export interface AnalysisPanelProps {
  result: XMLAnalysisResult;
  componentScores?: ComponentScores;
  /** Acoustic voice-quality metrics from the acoustic service (call-only). */
  voiceMetrics?: VoiceMetrics;
  /**
   * What the verification passes concluded. Undefined for analyses without
   * system metadata. Silent only when the gate stayed open AND the verifier
   * objected to nothing: since #752 a prose objection no longer blocks, so
   * `verified` on its own is not a reason to say nothing.
   */
  verification?: AnalysisVerificationState;
  /**
   * The weights of the time of analysis would score this call differently from
   * the project's current ones (#874) — the card then says the CRM and alert
   * number is not the one shown. `AnalysisData.scoredWithEarlierWeights`.
   */
  scoredWithEarlierWeights?: boolean;
  /**
   * The share of each criterion in this call's score when the project's
   * weights weight it (#874) — `AnalysisData.qualityWeightShares`.
   */
  qualityWeightShares?: Partial<Record<QualitySubMetricKey, number>>;
  /**
   * Resolved prompt-profile name that produced this analysis (Phase B, B-7)
   * — caller resolves analysis.promptProfileId against the org's profiles
   * list. Undefined/null hides the "Оценено по профилю" caption rather
   * than showing a raw id.
   */
  promptProfileName?: string | null;
  /** Resolved CRM stage name for the deal-stage badge. Undefined/null hides the badge. */
  crmStageName?: string | null;
  /** Resolved CRM pipeline name — shown only when `crmMultiplePipelines` is true. */
  crmPipelineName?: string | null;
  /** Org has more than one CRM pipeline — disambiguate the stage badge with the pipeline name. */
  crmMultiplePipelines?: boolean;
  /** Per-record deal-stage progression vs. the Phase B baseline (Phase C, C-3/C-4). */
  crmStageProgression?: CrmStageProgression | null;
  /** Render gate for the progression chip — null means "not evaluated yet". */
  crmProgressionEvaluatedAt?: string | null;
  diarization?: DiarizationSegment[];
  agentName?: string;
  /** Script adherence evaluation — rendered as a card in the "Скрипт" group. */
  scriptAdherence?: ScriptAdherenceData | null;
  scriptAdherenceLoading?: boolean;
  /** Результаты кастомных критериев — группа «Критерии» (#862). */
  criteria?: RecordCriterionResult[];
  criteriaLoading?: boolean;
  /** Запрос критериев не удался — группа говорит об этом вместо исчезновения. */
  criteriaFailed?: boolean;
  /**
   * Источник результата встроенных на записи (Э2, #2518): старая строка ключа
   * в «Речи» и «Комплаенсе» — только при `legacy`. Нет — Э2 выключен.
   */
  builtinSources?: BuiltinResultSources;
  /** Ключи встроенных на паузе Aelo: их старые строки — с подписью паузы. */
  builtinPausedByAelo?: readonly string[];
  /** Call category from record metadata — 'other' triggers the no-scores notice. */
  callCategory?: string | null;
  /** Record duration — hides a hallucinated out-of-range key-moment timecode (H-5). */
  durationSeconds?: number;
  /** Start a bounded evidence clip from a transcript-grounded key moment. */
  onPlaySegment?: (startSeconds: number, durationSeconds?: number) => void;
  /**
   * Quality-markers block. Resolved by the caller (RecordDetailInline), not
   * this panel — this panel stays presentational (names/data come from a
   * caller-resolved reference, never a raw id — see the provenance/stage
   * badge props above), while the review block itself fetches, mutates, and
   * shows toasts. Rendered as-is, after every section group: it's an action
   * on the analysis, not a metric of it, so it doesn't belong inside the
   * category-filtered groups above.
   */
  agentReviewSlot?: ReactNode;
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AnalysisPanel({
  result,
  componentScores,
  voiceMetrics,
  verification,
  scoredWithEarlierWeights,
  qualityWeightShares,
  promptProfileName,
  crmStageName,
  crmPipelineName,
  crmMultiplePipelines,
  crmStageProgression,
  crmProgressionEvaluatedAt,
  diarization,
  agentName,
  scriptAdherence,
  scriptAdherenceLoading,
  criteria,
  criteriaLoading,
  criteriaFailed,
  builtinSources,
  builtinPausedByAelo,
  callCategory,
  durationSeconds,
  onPlaySegment,
  agentReviewSlot,
  className,
}: AnalysisPanelProps) {
  const { t } = useTranslation(['records', 'promptBuilder']);
  const [activeCategory, setActiveCategory] = useState('all');
  const [verificationOpen, setVerificationOpen] = useState(false);

  // Панель не перемонтируется при переходе к записи, чей анализ уже в кеше, —
  // и раскрытый разбор переезжал бы на следующую запись, где его никто не
  // открывал. Правка состояния в рендере, а не эффектом: иначе чужой разбор
  // успевает мелькнуть кадром. Сравнение по ссылке, потому что `verification`
  // приходит из кеша запроса и между рендерами одной записи не пересоздаётся.
  const [shownVerification, setShownVerification] = useState(verification);
  if (shownVerification !== verification) {
    setShownVerification(verification);
    setVerificationOpen(false);
  }

  if (!result) {
    return (
      <div className={cn('p-6 text-center', className)}>
        <p className="text-fg-subtle">
          {t('records.analysisPanel.noAnalysis', 'No analysis available')}
        </p>
      </div>
    );
  }

  // Provenance line (spec §10, trimmed 2026-07-18): only the profile — the
  // one fact not visible elsewhere on the card. Project and category live in
  // the record header, the script is named inside its own adherence section.
  // A default profile is stored under its canonical English name; show the one
  // the "Categories and criteria" screen shows in this language (#1109).
  const provenanceLabel = `${t('records.analysisPanel.provenance.profile', 'Profile')}: ${
    promptProfileName
      ? getProfileDisplayName({ name: promptProfileName }, t)
      : t('records.analysisPanel.provenance.baseProfile', 'base profile')
  }`;

  const overallScore = result.summary?.qualityScore;
  // "Other" (personal/internal) intentionally gets no quality scores — explain
  // that instead of looking like a broken analysis. Chats now categorize
  // alongside calls, so the record-type restriction is gone. A customized
  // profile that re-enables scoring keeps the normal Hero score (no notice).
  const showOtherCategoryNotice = callCategory === 'other' && overallScore === undefined;
  const confidencePercent = confidenceLevelPercent(result.meta?.confidenceLevel);

  /**
   * Есть ли о чём предупреждать. Два повода, а не один: гейт закрыт ИЛИ
   * верификатор возразил против отдельных утверждений.
   *
   * Второй повод появился с #752: нарекание к прозе больше не закрывает
   * автоматизацию, и анализ бывает `verified` с замечаниями. Привязка показа к
   * статусу стёрла бы такие замечания с экрана — то есть ровно то, ради чего
   * вердикт начали сохранять целиком.
   *
   * Константа, а не булев флаг: она сужает тип, и ветки ниже видят состояние
   * заведомо непустым.
   */
  const verificationState =
    verification &&
    (verification.verificationStatus !== 'verified' ||
      verification.narrativeIssues.length > 0)
      ? verification
      : null;

  const verificationBadge = verificationState ? (
    <VerificationBadge
      verification={verificationState}
      expanded={verificationOpen}
      onToggle={() => setVerificationOpen((open) => !open)}
    />
  ) : undefined;

  // Group-visibility guards — mirror the section guards so a divider never
  // renders above an empty group.
  const hasScoring = !!(
    componentScores ||
    result.summary?.qualityScoreBreakdown ||
    result.textSummary ||
    result.structuredSummary ||
    result.content ||
    result.dialogue ||
    result.summary?.saleProbability != null ||
    (result.agreements && result.agreements.length > 0)
  );
  // "Скрипт" is the script evaluation and nothing else. Compliance and
  // strengths/recommendations come out of EVERY analysis, script or not — while
  // they lived here the group (and its filter pill) was permanently present, so
  // an org with no script attached was still offered a "Script rules" tab that
  // held only other groups' cards. They belong to Инсайты; the pill now appears
  // only when there is an adherence evaluation to show.
  const hasScript = !!(scriptAdherence || scriptAdherenceLoading);
  // «Речь» и «Инсайты» — тем же расчётом видимости, что их карточки (Э2,
  // #2518): строки, ушедшие к встроенным, не должны оставлять пустую вкладку.
  const hasSpeech = !!(
    (result.speech &&
      ((diarization?.length ?? 0) > 0 ||
        speechMetricsVisible(result, builtinSources, builtinPausedByAelo))) ||
    result.emotional ||
    voiceMetrics
  );
  // Нет критериев — нет группы (спека §7). Загрузка считается «есть»: иначе
  // группа появлялась бы рывком после того, как остальная панель уже собрана.
  // Неудача запроса — тоже «есть»: молчание тут значило бы «критериев нет», а
  // это другое утверждение (находка Codex на #1121).
  const hasCriteria = !!(
    (criteria && criteria.length > 0) ||
    criteriaLoading ||
    criteriaFailed
  );
  const hasInsights = !!(
    result.recommendations?.strengths?.length ||
    result.recommendations?.improvements?.length ||
    result.keyMoments?.length ||
    result.speech?.operatorErrors?.errors?.length ||
    visibleCompliance(result, builtinSources, builtinPausedByAelo)?.any ||
    result.businessIntelligence
  );

  // Category filter (Claude Design "Категории" structure). Each pill scopes the
  // panel to one group; "Все" shows everything with dividers. Only groups that
  // actually have data become pills, so the bar never offers an empty view.
  const categoryTabs: CategoryTab[] = [
    hasScoring && {
      key: 'scoring',
      label: t('records.analysisPanel.groups.scoring', 'Score'),
    },
    hasScript && {
      key: 'script',
      label: t('records.analysisPanel.groups.script', 'Script rules'),
    },
    hasCriteria && {
      key: 'criteria',
      label: t('records.analysisPanel.groups.criteria', 'Criteria'),
    },
    hasSpeech && {
      key: 'speech',
      label: t('records.analysisPanel.groups.speech', 'Speech'),
    },
    hasInsights && {
      key: 'insights',
      label: t('records.analysisPanel.groups.insights', 'Insights'),
    },
  ].filter(Boolean) as CategoryTab[];

  const showFilter = categoryTabs.length >= 2;
  // Fall back to "all" if the active category lost its data (e.g. result changed).
  const activeCat =
    showFilter && categoryTabs.some((c) => c.key === activeCategory)
      ? activeCategory
      : 'all';
  const groupVisible = (key: string) => activeCat === 'all' || activeCat === key;
  const dividersVisible = activeCat === 'all';

  // One element for both places the flag can stand (#2020).
  const attentionBlock = result.summary?.attentionFlag ? (
    <AttentionFlagBlock
      attentionReason={result.summary.attentionReason}
      attentionMoment={result.summary.attentionMoment}
      durationSeconds={durationSeconds}
      onPlaySegment={onPlaySegment}
    />
  ) : null;

  return (
    <div data-testid="analysis-panel" className={cn('flex flex-col gap-2.5', className)}>
      {/* "Other" category: scores intentionally suppressed — explain why */}
      {showOtherCategoryNotice && <OtherCategoryNotice />}

      {/* Hero overall score */}
      {overallScore !== undefined && (
        <HeroScore
          score={overallScore}
          attentionBlock={attentionBlock}
          scoredWithEarlierWeights={scoredWithEarlierWeights}
          weighted={qualityWeightShares !== undefined}
        />
      )}
      {/* No score, but the flag is raised: it stands on its own card (#2020) —
          inside HeroScore it vanished together with the score. */}
      {overallScore === undefined && attentionBlock && (
        <div className="rounded-card border border-border bg-bg-elevated p-4">
          {attentionBlock}
        </div>
      )}
      {/* No score with the current weights (every criterion of the call weighs
          0), yet the CRM got one: the note stands on its own (#874). */}
      {overallScore === undefined &&
        !showOtherCategoryNotice &&
        scoredWithEarlierWeights && (
          <WeightedAtAnalysisNote className="rounded-card border border-border bg-bg-elevated p-4" />
        )}

      {/* Состояние проверки — своя строка у карточки балла, а не слот внутри
          неё. Балла нет у категории «прочее» и у профиля с выключенными
          оценками: знак, вшитый в карточку, у таких записей исчезал бы вместе
          с ней, и понадобился бы второй, запасной способ его показать. Строка
          владеет знаком всегда, а карточка балла ничего не знает о проверке
          фактов (находка ревью на #753). */}
      {verificationBadge && <div className="flex justify-end">{verificationBadge}</div>}

      {/* Разбор проверки — под самим знаком и только по требованию.
          Плашка во всю ширину стояла здесь всегда и при 82% блокировок (#752)
          читалась как часть рамки, а не как предупреждение. */}
      {verificationState && verificationOpen && (
        <VerificationNotice verification={verificationState} />
      )}

      {/* Analysis provenance (§10) — the profile this analysis was scored
          with. StageBadge below only carries the CRM stage. */}
      <p data-testid="analysis-provenance" className="text-xs text-fg-muted">
        {provenanceLabel}
      </p>

      {/* Deal-stage badge (Phase B, B-7) */}
      <StageBadge
        stageName={crmStageName}
        pipelineName={crmPipelineName}
        multiplePipelines={crmMultiplePipelines}
      />

      {/* Deal-progression chip (Phase C, C-4) — hidden until evaluated */}
      <ProgressionChip
        progression={crmStageProgression}
        evaluatedAt={crmProgressionEvaluatedAt}
      />

      {/* Category filter pills */}
      {showFilter && (
        <CategoryFilter
          tabs={[
            { key: 'all', label: t('records.analysisPanel.groups.all', 'All') },
            ...categoryTabs,
          ]}
          active={activeCat}
          onChange={setActiveCategory}
        />
      )}

      {/* ===== Оценка ===== */}
      {hasScoring && groupVisible('scoring') && (
        <>
          {dividersVisible && (
            <GroupDivider label={t('records.analysisPanel.groups.scoring', 'Score')} />
          )}
          <QualityScoreSection
            result={result}
            componentScores={componentScores}
            confidencePercent={confidencePercent}
            weightShares={qualityWeightShares}
          />
          <SummarySection result={result} />
          <ContentSection result={result} />
          <DialogueSection result={result} />
          <SaleProbabilitySection result={result} />
          <AgreementsSection result={result} />
        </>
      )}

      {/* ===== Скрипт ===== */}
      {hasScript && groupVisible('script') && (
        <>
          {dividersVisible && (
            <GroupDivider
              label={t('records.analysisPanel.groups.script', 'Script rules')}
            />
          )}
          {/* No inner gate: `hasScript` above is exactly this condition — the
              adherence card is the whole group. */}
          <AnalysisSection
            title={t('records.analysisPanel.scriptAdherence', 'Script Adherence')}
            summary={
              scriptAdherence ? (
                <span
                  className={cn(
                    'font-mono text-xs font-semibold',
                    scoreTextColor(scriptAdherence.adherenceScore)
                  )}
                >
                  {scriptAdherence.adherenceScore}
                </span>
              ) : undefined
            }
          >
            <ScriptAdherenceTab
              adherence={scriptAdherence ?? null}
              loading={scriptAdherenceLoading}
            />
          </AnalysisSection>
        </>
      )}

      {/* ===== Критерии ===== */}
      {hasCriteria && groupVisible('criteria') && (
        <>
          {dividersVisible && (
            <GroupDivider
              label={t('records.analysisPanel.groups.criteria', 'Criteria')}
            />
          )}
          <CriteriaSection
            criteria={criteria ?? []}
            loading={criteriaLoading}
            failed={criteriaFailed}
            diarization={diarization}
            onPlaySegment={onPlaySegment}
          />
        </>
      )}

      {/* ===== Речь ===== */}
      {hasSpeech && groupVisible('speech') && (
        <>
          {dividersVisible && (
            <GroupDivider label={t('records.analysisPanel.groups.speech', 'Speech')} />
          )}
          <SpeechSection
            result={result}
            diarization={diarization}
            agentName={agentName}
            builtinSources={builtinSources}
            builtinPausedByAelo={builtinPausedByAelo}
          />
          <VoiceMetricsSection voiceMetrics={voiceMetrics} />
          <EmotionalSection result={result} />
        </>
      )}

      {/* ===== Инсайты ===== */}
      {hasInsights && groupVisible('insights') && (
        <>
          {dividersVisible && (
            <GroupDivider
              label={t('records.analysisPanel.groups.insights', 'Insights')}
            />
          )}
          <RecommendationsSection result={result} />
          <KeyMomentsSection
            result={result}
            durationSeconds={durationSeconds}
            onPlaySegment={onPlaySegment}
          />
          <ComplianceSection
            result={result}
            builtinSources={builtinSources}
            builtinPausedByAelo={builtinPausedByAelo}
          />
          <BusinessIntelligenceSection result={result} />
        </>
      )}

      {/* Quality-markers review — an action on the analysis, not a group of
          its metrics, so it renders after every group above rather than
          inside the category filter. */}
      {agentReviewSlot}
    </div>
  );
}
